// What the bag's customer strip says under the name, from what the till
// already holds: the customer record (last visit, birthday soon), the sizes
// learned from their bills, and their open wishes and follow-ups (client book).
import { profileFacts } from "./customerProfile";
import { daysOverdue, dueLabel } from "./clientBook";

// Open wishes, and the follow-up that is due soonest (overdue first).
export function clientBookCounts(rows) {
  const open = (rows || []).filter((r) => r && r.status === "OPEN");
  const wishes = open.filter((r) => r.kind === "WISH").length;
  const tasks = open
    .filter((r) => r.kind === "TASK" && r.dueDate)
    .sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)));
  return { wishes, followUp: tasks[0] || null };
}

// Facts for the strip, each { key, label, tone }: tone "warn" for a birthday
// this week or a follow-up due now, "bad" for an overdue one.
// Wishes for a piece now in the bag show as a hit; an alteration of theirs
// that is ready says so, with the date it was promised for.
export function bagClientFacts(customer, bunitId, { sizes = "", rows = null, bagProductIds = [], alterations = null, now = new Date() } = {}) {
  if (!customer) return [];
  const facts = profileFacts(customer, bunitId);
  const pick = (key) => facts.find((f) => f.key === key);
  const out = [];
  const lastVisit = pick("lastVisit");
  if (lastVisit) out.push({ key: "lastVisit", label: lastVisit.label, tone: "neu" });
  const soon = [pick("birthday"), pick("anniversary")].find((f) => f?.soon);
  if (soon) out.push({ key: soon.key, label: soon.label, tone: "warn" });
  if (sizes) out.push({ key: "sizes", label: `Sizes ${sizes}`, tone: "neu" });
  if (rows) {
    const { wishes, followUp } = clientBookCounts(rows);
    const inBag = new Set(bagProductIds || []);
    const hit = (rows || []).find((r) => r && r.status === "OPEN" && r.kind === "WISH" && r.productId && inBag.has(r.productId));
    if (hit) out.push({ key: "wishHit", label: `On their wishlist: ${hit.productName || "a piece in the bag"}`, tone: "good" });
    if (wishes > 0) out.push({ key: "wishes", label: `${wishes} on wishlist`, tone: "neu" });
    if (followUp) {
      const late = daysOverdue(followUp.dueDate, now) ?? 0;
      out.push({ key: "followUp", label: `Follow-up ${dueLabel(followUp.dueDate, now).toLowerCase()}`, tone: late > 0 ? "bad" : late === 0 ? "warn" : "neu" });
    }
  }
  const ready = readyAlterations(alterations, customer);
  if (ready.length > 0) {
    const first = ready[0];
    const day = first.promisedDate ? new Date(`${first.promisedDate}T00:00:00`) : null;
    const since = day && !Number.isNaN(day.getTime()) ? ` since ${day.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}` : "";
    out.push({ key: "alteration", label: ready.length > 1 ? `${ready.length} alterations ready${since}` : `Alteration ready${since}`, tone: "warn" });
  }
  return out;
}

// This customer's tickets that are ready to collect, oldest promise first.
export function readyAlterations(rows, customer) {
  if (!Array.isArray(rows) || !customer) return [];
  const id = customer.cwrCustomerId;
  const mob = String(customer.mobileNo || "").replace(/\D/g, "").slice(-10);
  return rows
    .filter((r) => r?.status === "RDY" && ((id && r.customerId === id) || (mob.length === 10 && String(r.mobileNo || "").replace(/\D/g, "").slice(-10) === mob)))
    .sort((a, b) => String(a.promisedDate).localeCompare(String(b.promisedDate)));
}
